import React, { FC, useEffect } from 'react';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { Toast } from 'react-bootstrap';
import colors from '@packages/core/styles/colors';
import { StyleSheet } from 'react-native';
import { createUseStyles } from 'react-jss';
import { CSSTransition } from 'react-transition-group';

export type ToasterType = 'success' | 'error' | 'info' | 'warning';

interface ToastNotificationProps {
    autoHideTime?: number;
    body: JSX.Element | string;
    closable?: boolean;
    iconName?: StsIconName;
    onClose: () => void;
    position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
    show: boolean;
    styles?: React.CSSProperties;
    textColor?: keyof typeof colors;
    variant?: 'success' | 'error' | 'info' | 'warning';
    type?: ToasterType;
}

const DEFAULT_AUTO_HIDE_TIME = 3000;
const DEFAULT_TEXT_COLOR: keyof typeof colors = 'black';
const DEFAULT_ICON: StsIconName = 'x-close';

const typeStyles = {
    success: { icon: 'check-circle-hollow', color: 'greenOne' },
    error: { icon: 'error', color: 'redFour' },
    info: { icon: 'info', color: 'blueOne' },
    warning: { icon: 'warning', color: 'yellowFour' },
};

const defaultStyles = StyleSheet.create({
    message: {
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 24,
    },
    closeButton: {
        backgroundColor: 'none',
    },
});

const useStyles = createUseStyles({
    body: {
        padding: '8px 16px',
    },
    toastContainer: {
        width: 'auto',
        textAlign: 'center',
        position: 'fixed',
        zIndex: 9999,
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
    },
});

const getPositionStyle = (position: ToastNotificationProps['position']) => {
    switch (position) {
        case 'top-left':
            return { top: 50, left: 50 };
        case 'top-center':
            return { top: 50, left: '50%', transform: 'translateX(-50%)' };
        case 'top-right':
            return { top: 50, right: 50 };
        case 'bottom-left':
            return { bottom: 50, left: 50 };
        case 'bottom-center':
            return { bottom: 50, left: '50%', transform: 'translateX(-50%)' };
        case 'bottom-right':
            return { bottom: 50, right: 50 };
        default:
            return { top: 50, right: 50 };
    }
};

const getBackgroundColor = (variant: ToastNotificationProps['variant']) => {
    switch (variant) {
        case 'success':
            return colors.textDarkPrimary;
        case 'error':
            return colors.redOne;
        case 'info':
            return colors.blueOne;
        case 'warning':
            return colors.yellowOne;
        default:
            return colors.greenOne;
    }
};

export const ToastNotification: FC<ToastNotificationProps> = ({
    autoHideTime = DEFAULT_AUTO_HIDE_TIME,
    body,
    closable = false,
    iconName,
    onClose,
    position = 'top-center',
    show,
    styles,
    textColor = 'white',
    variant = 'success',
    type,
}) => {
    const classes = useStyles();

    useEffect(() => {
        if (show && autoHideTime) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideTime);
            return () => clearTimeout(timer);
        }
    }, [show, autoHideTime, onClose]);

    const typeIcon = iconName || (type ? typeStyles[type].icon : DEFAULT_ICON);
    const typeTextColor = textColor || (type ? typeStyles[type].color : DEFAULT_TEXT_COLOR);

    const toastStyles: React.CSSProperties = {
        backgroundColor: getBackgroundColor(variant),
        ...(styles || {}),
    };

    const mergeTransform = (node: HTMLElement, newTransform: string) => {
        const currentTransform = node.style.transform || '';
        const cleanedTransform = currentTransform.replace(/translateY\([^)]+\)/, '').trim();
        node.style.transform = `${cleanedTransform} ${newTransform}`.trim();
    };

    return (
        <CSSTransition
            in={show}
            timeout={300}
            onEnter={(node: HTMLElement) => {
                mergeTransform(node, 'translateY(-120%)');
                node.style.opacity = '0';
            }}
            onEntering={(node: HTMLElement) => {
                mergeTransform(node, 'translateY(0)');
                node.style.opacity = '1';
                node.style.transition = 'transform 500ms ease, opacity 500ms ease';
            }}
            onExit={(node) => {
                mergeTransform(node, 'translateY(0)');
                node.style.opacity = '1';
            }}
            onExiting={(node) => {
                mergeTransform(node, 'translateY(-120%)');
                node.style.opacity = '0';
                node.style.transition = 'transform 500ms ease, opacity 500ms ease';
            }}
            unmountOnExit={true}
        >
            <Toast
                style={{
                    ...toastStyles,
                    ...getPositionStyle(position),
                }}
                className={classes.toastContainer}
                onClose={onClose}
                data-testid="toast-notification"
            >
                <Toast.Body className={classes.body}>
                    <div className="d-flex justify-content-between align-items-center">
                        {typeof body === 'string' ? (
                            <Typography variant="caption" color={typeTextColor} style={defaultStyles.message}>
                                {body}
                            </Typography>
                        ) : (
                            body
                        )}

                        {closable && (
                            <Button style={defaultStyles.closeButton} variant="ghost-gray" onPress={onClose}>
                                <Icon color={typeTextColor} name={typeIcon} />
                            </Button>
                        )}
                    </div>
                </Toast.Body>
            </Toast>
        </CSSTransition>
    );
};

export default ToastNotification;
